// src/pages/appointments/confirm/AppointmentConfirm.jsx
import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import TopBar from "@/sot/primitives/TopBar.jsx";
import TopBarCenter from "@/sot/primitives/TopBarCenter.jsx";
import Button from "@/components/ui/Button.jsx";
import Card from "@/components/ui/Card.jsx";
import Checkbox from "@/components/ui/Checkbox.jsx";
import { resolvePatientFacingDoctorName } from "@/utils/doctorPatientFacingName.js";
import Divider from "@/components/ui/Divider.jsx";
import { useAppointmentsFilters } from "@/pages/appointments/AppointmentsFiltersContext.jsx";
import { supabase } from "@/services/supabaseClient";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import {
  createAppointmentNotification,
  resolvePatientAuthUserId,
  resolveDoctorAuthUserId,
} from "@/services/ehrAccessService.js";
import { submitPatientAppointmentChangeRequest } from "@/services/appointmentChangeRequestService.js";
import { sendAppointmentEventEmail } from "@/services/appointmentEmailService.js";
import {
  APPOINTMENT_CHANGE_EVENT_TYPES,
  createAppointmentChangeEvent,
} from "@/services/appointmentChangeEvents.js";
import { normalizeVerificationStatus } from "@/utils/verificationStatus.js";
import { isCardPaymentMethod } from "@/services/appointmentCheckoutService.js";
import { normalizeCareSubjectFromBooking } from "@/services/dependantCareService.js";
import { normalizeDoctorPaymentMethodId } from "@/services/doctorPaymentPreferences.js";

const ICONS = {
  back: "/icons/doctor/appointments/arrow_back.svg",
  verified: "/icons/doctor/appointments/verified.svg",
  pendingVerification: "/icons/pending-verification.svg",
  schedule: "/icons/list-items/schedule_Fill.svg",
  type: "/icons/doctor/appointments/first_appointment.svg",
  evisit: "/icons/appointments/afrimed-EVisit.svg",
  facility: "/icons/doctor/appointments/hospital_Fill.svg",
};

function prettyPayment(id, t = (_, fallback) => fallback) {
  const map = {
    credit: t("appointments.confirm.payment.credit", "Credit / Debit cards"),
    bank: t("appointments.confirm.payment.bank", "Bank transfers"),
    cash: t("appointments.confirm.payment.cash", "Cash on service"),
    eft: t("appointments.confirm.payment.eft", "Instant EFT"),
    wallet: t("appointments.confirm.payment.wallet", "E-wallets"),
  };
  return map[id] || t("appointments.confirm.payment.fallback", "Payment method");
}

function resolveInsuranceContextLabel(booking, t = (_, fallback) => fallback) {
  const existing = String(booking?.insuranceContextLabel || "").trim();
  if (existing) return existing;
  const mode = String(booking?.insurancePaymentMode || "").trim().toLowerCase();
  if (mode === "insurance") {
    const provider = String(booking?.insuranceProvider || "").trim();
    return provider
      ? t("appointments.confirm.insurance.provider", "Insurance: {provider}", { provider })
      : t("appointments.confirm.insurance.generic", "Insurance selected");
  }
  return t("appointments.confirm.insurance.selfPay", "Self-pay");
}

function parseAmount(value) {
  const numeric = Number(value);
  if (Number.isFinite(numeric)) return numeric;
  if (typeof value === "string") {
    const match = value.match(/-?\d+(\.\d+)?/);
    if (match) {
      const parsed = Number(match[0]);
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return null;
}

function resolveBookingAmount(booking, doctor) {
  const candidates = [
    booking?.booked_service_price,
    booking?.amountPaid,
    booking?.amount_paid,
    booking?.amount,
    booking?.servicePrice,
    booking?.service_price,
    booking?.serviceNamePrice,
    booking?.serviceLabelPrice,
    booking?.selectedService?.price,
    booking?.service?.price,
    booking?.doctor?.servicePrice,
    booking?.doctor?.service_price,
    booking?.doctor?.serviceNamePrice,
    booking?.doctor?.serviceLabelPrice,
    doctor?.servicePrice,
    doctor?.service_price,
    doctor?.serviceNamePrice,
    doctor?.serviceLabelPrice,
  ];

  for (const candidate of candidates) {
    const amount = parseAmount(candidate);
    if (amount != null) return amount;
  }

  return 0;
}

function safeJSONParse(raw) {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function hasExplicitCareSubjectState(state) {
  if (!state || typeof state !== "object") return false;
  return Boolean(
    state?.careSubject ||
      state?.careSubjectType ||
      state?.careSubjectId ||
      state?.careSubjectName ||
      state?.careSubjectRelationship ||
      state?.care_subject_type ||
      state?.care_subject_dependant_id ||
      state?.care_subject_relationship,
  );
}

function mergeCareSubjectFromStored(locationState, storedState) {
  if (!locationState || typeof locationState !== "object") return storedState || null;
  if (!storedState || typeof storedState !== "object") return locationState;
  if (hasExplicitCareSubjectState(locationState) && !hasExplicitCareSubjectState(storedState)) {
    return locationState;
  }

  return {
    ...storedState,
    ...locationState,
    careSubject: storedState?.careSubject ?? locationState?.careSubject ?? null,
    careSubjectType: storedState?.careSubjectType ?? locationState?.careSubjectType ?? null,
    careSubjectId: storedState?.careSubjectId ?? locationState?.careSubjectId ?? null,
    careSubjectName: storedState?.careSubjectName ?? locationState?.careSubjectName ?? "",
    careSubjectRelationship:
      storedState?.careSubjectRelationship ?? locationState?.careSubjectRelationship ?? "",
    careSubjectDateOfBirth:
      storedState?.careSubjectDateOfBirth ?? locationState?.careSubjectDateOfBirth ?? null,
    careSubjectVerificationStatus:
      storedState?.careSubjectVerificationStatus ??
      locationState?.careSubjectVerificationStatus ??
      "",
  };
}

function readBooking(id, locationState) {
  if (locationState?.__flow === "reschedule") return null;

  const bookingKey = String(locationState?.bookingKey || "").trim();
  let storedState = null;

  try {
    if (bookingKey) {
      const rawByKey = sessionStorage.getItem(`afrimed:bookingKey:${bookingKey}`);
      const parsedByKey = rawByKey ? safeJSONParse(rawByKey) : null;
      if (parsedByKey) storedState = parsedByKey;

      const rawByKeyLocal = localStorage.getItem(`afrimed:bookingKey:${bookingKey}`);
      const parsedByKeyLocal = rawByKeyLocal ? safeJSONParse(rawByKeyLocal) : null;
      if (parsedByKeyLocal && !storedState) storedState = parsedByKeyLocal;
    }
    if (!storedState && id) {
      const rawById = sessionStorage.getItem(`afrimed:booking:${id}`);
      const parsedById = rawById ? safeJSONParse(rawById) : null;
      if (parsedById) storedState = parsedById;

      const rawByIdLocal = localStorage.getItem(`afrimed:booking:${id}`);
      const parsedByIdLocal = rawByIdLocal ? safeJSONParse(rawByIdLocal) : null;
      if (parsedByIdLocal && !storedState) storedState = parsedByIdLocal;
    }
    if (!storedState) {
      const rawLast = sessionStorage.getItem("afrimed:booking:last");
      const parsedLast = rawLast ? safeJSONParse(rawLast) : null;
      if (parsedLast) storedState = parsedLast;
    }
    if (!storedState) {
      const rawLastLocal = localStorage.getItem("afrimed:booking:last");
      storedState = rawLastLocal ? safeJSONParse(rawLastLocal) : null;
    }
  } catch {
    storedState = null;
  }

  if (locationState?.doctor || locationState?.selectedDate || locationState?.selectedTime) {
    const merged = mergeCareSubjectFromStored(locationState, storedState);
    return {
      ...merged,
      bookingKey: bookingKey || merged?.bookingKey || null,
      doctorId: merged?.doctorId || merged?.doctor?.id || id || null,
    };
  }

  return storedState;
}

function readReschedule(id, locationState) {
  if (locationState?.__flow === "reschedule") return locationState;

  const keys = [
    `afrimed:appointment:${String(id)}`,
    `afrimed:doctor_appt:${String(id)}`,
    "afrimed:appointment:last",
  ];

  for (const key of keys) {
    try {
      const raw = sessionStorage.getItem(key);
      const parsed = raw ? safeJSONParse(raw) : null;
      const parsedId = String(parsed?.appointmentId || parsed?.id || "").trim();
      const routeId = String(id || "").trim();
      if (key === "afrimed:appointment:last" && parsedId && routeId && parsedId !== routeId) {
        continue;
      }
      if (parsed?.selectedDate || parsed?.selectedTime || parsed?.when?.dateISO) return parsed;
    } catch {}
  }

  return null;
}

function toAppointmentISO(dateISO, timeHHMM) {
  const date = String(dateISO || "").trim();
  const time = String(timeHHMM || "").trim();
  if (!date || !time) return null;

  const candidate = new Date(`${date}T${time}:00`);
  if (Number.isNaN(candidate.getTime())) return null;
  return candidate.toISOString();
}

function formatDisplayTime(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/[ap]m/i.test(raw)) return raw.replace(/\s+/g, " ").trim();

  const match = raw.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return raw;

  const hours24 = Number(match[1]);
  const minutes = match[2];
  if (!Number.isFinite(hours24)) return raw;

  const suffix = hours24 >= 12 ? "PM" : "AM";
  const hours12 = hours24 % 12 || 12;
  return `${hours12}:${minutes} ${suffix}`;
}

function addMinutes(timeHHMM, minutesToAdd) {
  const match = String(timeHHMM || "").trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return "";
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return "";
  const total = hours * 60 + minutes + Number(minutesToAdd || 0);
  const normalized = ((total % 1440) + 1440) % 1440;
  const nextHours = String(Math.floor(normalized / 60)).padStart(2, "0");
  const nextMinutes = String(normalized % 60).padStart(2, "0");
  return `${nextHours}:${nextMinutes}`;
}

function normalizeDbAppointmentStatus(rawStatus, fallback = "scheduled") {
  const raw = String(rawStatus || "").trim().toLowerCase();
  if (
    raw === "pending" ||
    raw === "scheduled" ||
    raw === "confirmed" ||
    raw === "in_progress" ||
    raw === "completed" ||
    raw === "cancelled"
  ) {
    return raw;
  }
  return fallback;
}

function resolveRescheduleMode(reschedule) {
  const explicit = String(reschedule?.mode || reschedule?.visitMode || "").trim().toLowerCase();
  if (explicit === "evisit" || explicit === "inperson") return explicit;
  return String(reschedule?.consultation_type || "").trim().toLowerCase() === "private"
    ? "evisit"
    : "inperson";
}

function consultationTypeFromMode(mode, fallbackType) {
  if (mode === "evisit") return "private";
  const raw = String(fallbackType || "").trim().toLowerCase();
  if (raw && raw !== "private") return raw;
  return "moh";
}

function normalizeVisitMode(mode, consultationType) {
  const explicit = String(mode || "").trim().toLowerCase();
  if (explicit === "evisit" || explicit === "inperson") return explicit;
  return String(consultationType || "").trim().toLowerCase() === "private" ? "evisit" : "inperson";
}

function formatVisitModeLabel(mode, labels = {}) {
  return normalizeVisitMode(mode) === "evisit"
    ? labels.evisit || "Virtual"
    : labels.inperson || "in-person";
}

function buildDoctorRescheduleNotificationMessage({
  patientDisplayName,
  previousMode,
  nextMode,
  labels = {},
}) {
  const name = String(patientDisplayName || labels.patientFallback || "Patient").trim() ||
    labels.patientFallback ||
    "Patient";
  const previous = normalizeVisitMode(previousMode);
  const next = normalizeVisitMode(nextMode);

  if (previous !== next) {
    return `${name} ${labels.modeChangeLead || "requested to change from"} ${formatVisitModeLabel(previous, labels)} ${labels.modeChangeTo || "to"} ${formatVisitModeLabel(next, labels)}. ${labels.pendingApproval || "Pending for approval."}`;
  }

  return `${name} ${labels.newTimeLead || "requested a new appointment time."} ${labels.pendingApproval || "Pending for approval."}`;
}

function normalizeDurationMinutes(value, fallback = 30) {
  const minutes = Number(value);
  if (Number.isFinite(minutes) && minutes > 0) return Math.round(minutes);
  return fallback;
}

function calcAge(dobISO) {
  if (!dobISO) return null;
  const d = new Date(dobISO);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
  return age >= 0 && age <= 120 ? age : null;
}

function persistSnapshots(id, payload) {
  if (!payload) return;
  try {
    sessionStorage.setItem(`afrimed:appointment:${String(id)}`, JSON.stringify(payload));
    sessionStorage.setItem("afrimed:appointment:last", JSON.stringify(payload));
    sessionStorage.setItem(`afrimed:doctor_appt:${String(id)}`, JSON.stringify(payload));
    localStorage.setItem(`afrimed:appointment:${String(id)}`, JSON.stringify(payload));
    localStorage.setItem("afrimed:appointment:last", JSON.stringify(payload));
    localStorage.setItem(`afrimed:doctor_appt:${String(id)}`, JSON.stringify(payload));
  } catch {}
}

export default function AppointmentConfirm() {
  const nav = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const auth = useAuth() || {};
  const { t, languageCode, formatDate } = useLanguage();
  const authProfile = auth.profile || auth.user?.user_metadata || {};

  const { selectedDate: ctxSelectedDate, selectedTime: ctxSelectedTime } = useAppointmentsFilters();
  const booking = React.useMemo(() => {
    const resolved = readBooking(id, location.state);
    if (!resolved) return resolved;
    const paymentMethod = normalizeDoctorPaymentMethodId(resolved.paymentMethod);
    return paymentMethod ? { ...resolved, paymentMethod } : resolved;
  }, [id, location.state]);
  const reschedule = React.useMemo(() => readReschedule(id, location.state), [id, location.state]);
  const isReschedule =
    location.state?.__flow === "reschedule" ||
    (!!reschedule && (!booking || String(location.state?.actorRole || "") === "doctor"));

  const bookingSelectedDate = ctxSelectedDate || booking?.selectedDate || null;
  const bookingSelectedTime = ctxSelectedTime || booking?.selectedTime || null;
  const paymentMethod = booking?.paymentMethod || "credit";
  const doctor = booking?.doctor || null;
  const doctorId = String(id || booking?.doctorId || doctor?.id || "").trim();
  const bookedDoctor = React.useMemo(
    () => (doctor ? { ...doctor, id: doctor?.id || doctorId || null } : doctorId ? { id: doctorId } : null),
    [doctor, doctorId]
  );
  const canConfirmBooking = !!bookingSelectedDate && !!bookingSelectedTime && !!doctorId;
  const requiresCardCheckout = isCardPaymentMethod(paymentMethod);

  const paymentLabel = booking?.paymentMethodLabel || prettyPayment(paymentMethod, t);
  const insuranceContextLabel = resolveInsuranceContextLabel(booking, t);
  const amountPaid = resolveBookingAmount(booking, bookedDoctor);
  const currency =
    booking?.currency ||
    bookedDoctor?.currency ||
    "ZAR";
  const serviceId = booking?.serviceId || null;
  const serviceName =
    booking?.serviceName ||
    bookedDoctor?.serviceLabel ||
    bookedDoctor?.serviceName ||
    t("appointments.confirm.serviceFallback", "Consultation");
  const serviceNamePrice =
    booking?.serviceNamePrice ||
    (amountPaid > 0 ? `${amountPaid.toFixed(2)} ${currency}` : "");
  const bookingDurationMinutes = normalizeDurationMinutes(
    booking?.durationMinutes ?? booking?.duration_minutes ?? booking?.duration,
    30,
  );
  const mode = booking?.mode || null;
  const facilityId = booking?.facilityId || booking?.facility?.id || null;
  const facilityName = booking?.facilityName || booking?.facility?.name || "";
  const facility = booking?.facility || null;
  const consultationType =
    booking?.consultationType ||
    booking?.consultation_type ||
    (mode === "evisit" ? "private" : "moh");
  const guardianName = String(
    auth?.profile?.full_name ||
      auth?.profile?.display_name ||
      auth?.user?.user_metadata?.full_name ||
      t("appointments.confirm.patientFallback", "Patient"),
  ).trim() || t("appointments.confirm.patientFallback", "Patient");
  const careSubject = React.useMemo(
    () => normalizeCareSubjectFromBooking(booking || {}, auth),
    [auth, booking],
  );
  const patientDisplayName =
    String(careSubject?.patientName || guardianName).trim() || guardianName;
  const bookingOwnerName =
    String(careSubject?.guardianName || guardianName).trim() || guardianName;
  const careSubjectConfirmationRequired =
    String(careSubject?.type || "self").trim().toLowerCase() === "dependant" &&
    Boolean(String(patientDisplayName || "").trim());
  const bookingRouteState = React.useMemo(
    () => ({
      ...(booking || location.state || {}),
      bookingKey: booking?.bookingKey || location.state?.bookingKey || null,
      doctor: doctor || booking?.doctor || null,
      doctorId,
    }),
    [booking, doctor, doctorId, location.state]
  );

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState("");
  const [careSubjectConfirmed, setCareSubjectConfirmed] = React.useState(
    !careSubjectConfirmationRequired,
  );

  React.useEffect(() => {
    setCareSubjectConfirmed(!careSubjectConfirmationRequired);
  }, [careSubjectConfirmationRequired, patientDisplayName]);

  function handleBackToBooking() {
    if (!doctorId) {
      nav(-1);
      return;
    }

    nav(`/appointments/book/${doctorId}`, {
      state: bookingRouteState,
    });
  }

  if (!isReschedule) {
    return (
      <div className="min-h-[100dvh] bg-[rgb(var(--clr-surface-container-low))]">
        <div className="afrimed-rail afrimed-rail--comfortable pt-4 pb-24">
          <TopBarCenter
            title={t("appointments.confirm.title", "Appointment summary")}
            close
            onBack={handleBackToBooking}
          />

          <Card className="mt-4 overflow-hidden">
            <div className="px-4 py-4">
              <div className="text-label-large text-on-surface-var mb-2">
                {t("appointments.confirm.title", "Appointment summary")}
              </div>

              <div className="space-y-2 text-body-large text-on-surface">
                <div className="flex justify-between gap-4">
                  <span className="text-on-surface-var">
                    {t("appointments.confirm.fields.doctor", "Clinician")}
                  </span>
                  <span className="text-right">
                    {doctor
                      ? resolvePatientFacingDoctorName(doctor, doctor?.name)
                      : doctorId
                        ? `ID: ${doctorId}`
                        : "—"}
                  </span>
                </div>

                <div className="flex justify-between gap-4">
                  <span className="text-on-surface-var">
                    {t("appointments.confirm.fields.service", "Service")}
                  </span>
                  <span className="text-right">{serviceName}</span>
                </div>

                <div className="flex justify-between gap-4">
                  <span className="text-on-surface-var">
                    {t("appointments.confirm.fields.patient", "Patient")}
                  </span>
                  <span className="text-right">{patientDisplayName}</span>
                </div>

                {careSubject?.type === "dependant" ? (
                  <div className="flex justify-between gap-4">
                    <span className="text-on-surface-var">
                      {t("appointments.confirm.fields.bookingOwner", "Booking owner")}
                    </span>
                    <span className="text-right">{bookingOwnerName}</span>
                  </div>
                ) : null}

                <div className="flex justify-between gap-4">
                  <span className="text-on-surface-var">
                    {t("appointments.confirm.fields.date", "Date")}
                  </span>
                  <span className="text-right">
                    {bookingSelectedDate
                      ? formatDate(bookingSelectedDate, {
                          weekday: "long",
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                        })
                      : "—"}
                  </span>
                </div>

                <div className="flex justify-between gap-4">
                  <span className="text-on-surface-var">
                    {t("appointments.confirm.fields.time", "Time")}
                  </span>
                  <span className="text-right">{bookingSelectedTime || "—"}</span>
                </div>

                <div className="flex justify-between gap-4">
                  <span className="text-on-surface-var">
                    {t("appointments.confirm.fields.amountToPay", "Amount to pay")}
                  </span>
                  <span className="text-right">
                    {amountPaid > 0 ? `${currency} ${amountPaid.toFixed(2)}` : "—"}
                  </span>
                </div>

                <div className="flex justify-between gap-4">
                  <span className="text-on-surface-var">
                    {t("appointments.confirm.fields.paymentCover", "Payment cover")}
                  </span>
                  <span className="text-right">{insuranceContextLabel}</span>
                </div>
              </div>
            </div>

            {submitError ? (
              <div className="px-4 pt-4">
                <div className="rounded-[10px] bg-[rgba(220,38,38,0.08)] px-3 py-2 text-body-medium text-[rgb(var(--schemes-critical))]">
                  {submitError}
                </div>
              </div>
            ) : null}
          </Card>

          {careSubjectConfirmationRequired ? (
            <label className="mt-4 flex items-center gap-3 rounded-[10px] bg-[rgba(var(--afrimed-primary),0.08)] px-3 py-3 text-body-medium text-on-surface">
              <Checkbox
                checked={careSubjectConfirmed}
                onChange={() => setCareSubjectConfirmed((prev) => !prev)}
                className="shrink-0 self-center"
                ariaLabel={t(
                  "appointments.confirm.confirmCareSubject",
                  "I confirm this appointment is for {name}.",
                  { name: patientDisplayName || t("appointments.confirm.patientFallback", "Patient") },
                )}
              />
              <span>
                {t(
                  "appointments.confirm.confirmCareSubject",
                  "I confirm this appointment is for {name}.",
                  { name: patientDisplayName || t("appointments.confirm.patientFallback", "Patient") },
                )}
              </span>
            </label>
          ) : null}

          <div className="mt-4 flex justify-center">
            <Button
              size="cta"
              className="w-full md:w-auto md:min-w-[380px]"
              disabled={!canConfirmBooking || isSubmitting || (careSubjectConfirmationRequired && !careSubjectConfirmed)}
              onClick={() => {
                if (careSubjectConfirmationRequired && !careSubjectConfirmed) {
                  setSubmitError(t("appointments.confirm.errors.confirmCareSubjectBeforeProceed", "Please confirm this appointment is for {name} before continuing.", { name: patientDisplayName || t("appointments.confirm.patientFallback", "Patient") }));
                  return;
                }
                // Preserve the normalised booking identity the checkout recovery path expects.
                // A raw location state can omit the resolved doctor/booking key after a re-entry.
                nav(doctorId ? `/appointments/payment/${doctorId}` : "/appointments/payment", { state: bookingRouteState });
              }}
            >
              {t("appointments.confirm.continueToCheckout", "Continue to checkout")}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const actorRole = reschedule?.actorRole === "doctor" ? "doctor" : "patient";
  const patient = reschedule?.patient || reschedule?.patientProfile || null;
  const counterpartVerificationStatus = normalizeVerificationStatus(
    actorRole === "doctor"
      ? (
          patient?.id_verification_status ||
          patient?.verification_status ||
          reschedule?.patient?.id_verification_status ||
          reschedule?.patient?.verification_status ||
          reschedule?.patientProfile?.id_verification_status ||
          reschedule?.patientProfile?.verification_status ||
          reschedule?.id_verification_status ||
          reschedule?.verification_status ||
          ""
        )
      : (
          reschedule?.doctor?.id_verification_status ||
          reschedule?.doctor?.verification_status ||
          (reschedule?.doctor?.verified ? "verified" : "") ||
          ""
        ),
    actorRole === "doctor" ? "pending" : "verified"
  );
  const counterpartVerificationIcon =
    counterpartVerificationStatus === "verified"
      ? ICONS.verified
      : ICONS.pendingVerification;
  const counterpartName =
    actorRole === "doctor"
      ? patient?.full_name || patient?.name || reschedule?.patient_name || reschedule?.patientName || t("appointments.confirm.patientFallback", "Patient")
      : resolvePatientFacingDoctorName(
          reschedule?.doctor,
          reschedule?.doctor?.name,
          reschedule?.doctor?.full_name,
          t("appointments.confirm.doctorFallback", "Doctor")
        );
  const counterpartAvatar =
    actorRole === "doctor"
      ? patient?.avatar_url || patient?.avatar || reschedule?.patient_avatar || "/icons/circular-avatar-placeholder.svg"
      : reschedule?.doctor?.avatar || reschedule?.doctor?.photo_url || "/icons/circular-avatar-placeholder.svg";
  const patientSex =
    patient?.sex || patient?.gender || reschedule?.sex || reschedule?.gender || null;
  const patientDob =
    patient?.dob || patient?.date_of_birth || patient?.dateOfBirth || reschedule?.dob || reschedule?.date_of_birth || null;
  const patientAge = calcAge(patientDob);
  const counterpartMeta =
    actorRole === "doctor"
      ? [patientAge != null ? `${patientAge} Years old` : null, patientSex ? `${String(patientSex).charAt(0).toUpperCase()}${String(patientSex).slice(1)}` : null]
          .filter(Boolean)
          .join(", ")
      : reschedule?.doctor?.specialty || reschedule?.doctor?.subtitle || t("appointments.confirm.doctorFallback", "Doctor");

  const rescheduleMode = resolveRescheduleMode(reschedule);
  const previousRescheduleMode = normalizeVisitMode(
    reschedule?.previousMode || reschedule?.previous_mode,
    reschedule?.previousConsultationType || reschedule?.previous_consultation_type || reschedule?.consultation_type
  );
  const isVisitTypeChange = previousRescheduleMode !== rescheduleMode;
  const isEvisit = rescheduleMode === "evisit";
  const selectedDate = reschedule?.selectedDate || reschedule?.when?.dateISO || null;
  const selectedTime = reschedule?.selectedTime || reschedule?.when?.time || "";
  const durationMinutes =
    Number(reschedule?.duration_minutes || reschedule?.durationMinutes || reschedule?.duration || 15) || 15;
  const endTime =
    selectedTime ? addMinutes(selectedTime, durationMinutes) : "";
  const nextConsultationType = consultationTypeFromMode(
    rescheduleMode,
    reschedule?.consultation_type
  );
  const nextPracticeId = isEvisit
    ? null
    : reschedule?.facilityId || reschedule?.facility?.id || reschedule?.practice_id || null;
  const nextStatus = normalizeDbAppointmentStatus(reschedule?.status, "scheduled");
  const typeLabel =
    reschedule?.serviceName ||
    reschedule?.serviceLabel ||
    reschedule?.subtitle ||
    reschedule?.appointment_type ||
    reschedule?.typeLabel ||
    (isEvisit
      ? t("appointments.edit.mode.eVisit", "Virtual")
      : t("appointments.confirm.firstConsultancy", "First time consultancy"));
  const rescheduleAmount = resolveBookingAmount(reschedule, reschedule?.doctor || null);
  const rescheduleCurrency =
    reschedule?.booked_service_currency ||
    reschedule?.currency ||
    reschedule?.doctor?.currency ||
    "BWP";
  const amountLabel =
    rescheduleAmount > 0
      ? `${rescheduleAmount.toFixed(2)} ${rescheduleCurrency}`
      : reschedule?.serviceNamePrice || reschedule?.doctor?.serviceNamePrice || "—";
  const placeLabel = isEvisit
    ? t("appointments.confirm.videoConsultation", "Video consultation")
    : reschedule?.facilityName ||
      reschedule?.facility?.name ||
      reschedule?.facility_name ||
      t("appointments.confirm.facilityFallback", "Life Gaborone Private Hospital");
  const placeIcon = isEvisit ? ICONS.evisit : ICONS.facility;
  const canConfirmReschedule = !!id && !!selectedDate && !!selectedTime;
  const rescheduleTitle = isVisitTypeChange
    ? t("appointments.confirm.visitTypeChange", "Visit type change")
    : t("appointments.confirm.newTimeRequest", "New time request");

  async function handleRescheduleConfirm() {
    if (!canConfirmReschedule || isSubmitting) return;

    setIsSubmitting(true);
    setSubmitError("");

    const appointmentDateIso = toAppointmentISO(selectedDate, selectedTime);
    if (!appointmentDateIso) {
      setSubmitError(t("appointments.confirm.errors.invalidDateTime", "Invalid appointment date/time."));
      setIsSubmitting(false);
      return;
    }

    try {
      const { data: currentAppointment, error: currentAppointmentError } = await supabase
        .from("appointments")
        .select("id, doctor_id, patient_id, appointment_date, status")
        .eq("id", id)
        .maybeSingle();
      if (currentAppointmentError) throw currentAppointmentError;
      if (!currentAppointment?.id) {
        throw new Error(t("appointments.confirm.errors.update", "Unable to update appointment."));
      }

      const actorUserId = String(auth?.user?.id || "").trim();
      if (!actorUserId) {
        throw new Error("Please sign in again.");
      }

      if (actorRole === "patient") {
        // This is one server-owned transaction: it persists the proposed
        // change and the clinician's actionable notification together.  It
        // must not fall through to the legacy best-effort notification call.
        await submitPatientAppointmentChangeRequest({
          appointmentId: currentAppointment.id,
          proposedAppointmentDate: appointmentDateIso,
          consultationType: nextConsultationType,
          correlationId: `patient-appointment-change:${currentAppointment.id}:${Date.now()}`,
        });

        try {
          // The server resolves the clinician notification first, so the
          // email CTA can always point to the same decision surface.
          await sendAppointmentEventEmail({
            appointmentId: currentAppointment.id,
            eventType: "reschedule_requested",
          });
        } catch (emailError) {
          console.warn("Appointment change email could not be sent after the actionable notification was created:", emailError);
        }

        try {
          sessionStorage.setItem(`afrimed:toast:appointmentRescheduleRequested:${String(id)}`, "1");
        } catch {}
        window.dispatchEvent(new Event("afrimed:appointments:changed"));
        window.dispatchEvent(new Event("afrimed:notifications:changed"));
        nav(`/appointments/details/${id}`, {
          replace: true,
          state: { justRequestedReschedule: true },
        });
        return;
      }

      await createAppointmentChangeEvent({
        appointmentId: currentAppointment.id,
        eventType: APPOINTMENT_CHANGE_EVENT_TYPES.RESCHEDULE_REQUESTED,
        oldAppointmentDate: currentAppointment.appointment_date,
        newAppointmentDate: appointmentDateIso,
        actorUserId,
        payload: {
          appointment_date: appointmentDateIso,
          consultation_type: nextConsultationType,
          practice_id: nextPracticeId,
          duration_minutes: durationMinutes,
          booked_service_id:
            reschedule?.booked_service_id ||
            reschedule?.booked_serviceId ||
            reschedule?.selectedService?.id ||
            reschedule?.service?.id ||
            null,
          booked_service_name: typeLabel || null,
          booked_service_price: rescheduleAmount > 0 ? rescheduleAmount : null,
          booked_service_currency: rescheduleCurrency || null,
          status: nextStatus,
        },
      });
      try {
        sessionStorage.setItem(`afrimed:toast:appointmentRescheduleRequested:${String(id)}`, "1");
      } catch {}

      const patientAuthId = String(
        await resolvePatientAuthUserId({
          appointmentId: String(currentAppointment?.id || id || "").trim(),
          patientUserId: currentAppointment?.patient_id || "",
          patientRef: currentAppointment?.patient_id || "",
        })
      ).trim();
      const doctorAuthId = String(
        await resolveDoctorAuthUserId({
          appointmentId: String(currentAppointment?.id || id || "").trim(),
          doctorUserId:
            currentAppointment?.doctor_id ||
            reschedule?.doctor?.id ||
            reschedule?.doctorId ||
            reschedule?.doctor_id ||
            "",
          doctorRef:
            reschedule?.doctor?.id ||
            reschedule?.doctorId ||
            reschedule?.doctor_id ||
            currentAppointment?.doctor_id ||
            "",
        })
      ).trim();
      const patientDisplayName =
        String(
          authProfile?.full_name ||
          [authProfile?.first_name, authProfile?.last_name].filter(Boolean).join(" ") ||
          patient?.full_name ||
          patient?.name ||
          t("appointments.confirm.patientFallback", "Patient")
        ).trim() || t("appointments.confirm.patientFallback", "Patient");

      if (actorRole === "patient" && doctorAuthId) {
        try {
          await createAppointmentNotification({
            userId: doctorAuthId,
            title: isVisitTypeChange
              ? t("appointments.confirm.notifications.visitTypeChangeTitle", "Visit type change")
              : t("appointments.confirm.notifications.newTimeRequestTitle", "New time request"),
            message: buildDoctorRescheduleNotificationMessage({
              patientDisplayName,
              previousMode: previousRescheduleMode,
              nextMode: rescheduleMode,
              labels: {
                patientFallback: t("appointments.confirm.patientFallback", "Patient"),
                evisit: t("appointments.edit.mode.eVisit", "Virtual"),
                inperson: t("appointments.confirm.inPersonLower", "in-person"),
                modeChangeLead: t("appointments.confirm.notifications.modeChangeLead", "requested to change from"),
                modeChangeTo: t("appointments.confirm.notifications.modeChangeTo", "to"),
                newTimeLead: t("appointments.confirm.notifications.newTimeLead", "requested a new appointment time."),
                pendingApproval: t("appointments.confirm.notifications.pendingApproval", "Pending for approval."),
              },
            }),
            type: `appointment_reschedule_request:${String(id)}`,
            priority: "urgent",
          });
        } catch (notificationError) {
          console.warn("Unable to create doctor reschedule notification:", notificationError);
        }
      }

      if (actorRole === "doctor" && patientAuthId) {
        try {
          await createAppointmentNotification({
            userId: patientAuthId,
            title: t("appointments.confirm.notifications.appointmentUpdateTitle", "Appointment update"),
            message: t(
              "appointments.confirm.notifications.appointmentUpdateMessage",
              "Your doctor proposed a new appointment time. Review and confirm the change."
            ),
            type: `appointment_reschedule_request:${String(id)}`,
            priority: "urgent",
          });
        } catch (notificationError) {
          console.warn("Unable to create patient reschedule notification:", notificationError);
        }
      }

      window.dispatchEvent(new Event("afrimed:appointments:changed"));
      window.dispatchEvent(new Event("afrimed:notifications:changed"));

      if (actorRole === "doctor") {
        nav(`/doctors/appointments/${id}`, {
          replace: true,
          state: {
            appointmentId: id,
            id,
            justRequestedReschedule: true,
          },
        });
        return;
      }

      nav(`/appointments/details/${id}`, {
        replace: true,
        state: { justRequestedReschedule: true },
      });
    } catch (error) {
      setSubmitError(
        error?.message || t("appointments.confirm.errors.update", "Unable to update appointment.")
      );
      setIsSubmitting(false);
      return;
    }

    setIsSubmitting(false);
  }

  return (
    <div className="min-h-[100dvh] bg-[rgb(var(--clr-surface-container-low))]" data-hide-tabbar="true">
      <div className="afrimed-rail afrimed-rail--comfortable pt-4 pb-24 md:pb-28">
        <TopBar
          title={rescheduleTitle}
          onBack={() => nav(-1)}
        />

        <Card className="mt-6 px-4 py-4">
          <div className="flex items-center gap-3">
            <img
              src={counterpartAvatar}
              alt={counterpartName}
              className="h-11 w-11 rounded-full object-cover shrink-0"
              draggable="false"
            />

            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <div className="truncate text-body-large text-on-surface">{counterpartName}</div>
                <img
                  src={counterpartVerificationIcon}
                  alt=""
                  className="h-4 w-4 shrink-0"
                  draggable="false"
                />
              </div>
              <div className="truncate text-body-medium text-on-surface-var">
                {counterpartMeta || "—"}
              </div>
            </div>
          </div>
        </Card>

        <div className="mt-4 text-body-medium text-on-surface-var">
          {t("appointments.confirm.schedule", "Schedule")}
        </div>

        <Card className="mt-2 overflow-hidden">
          <div className="px-4 py-3 flex min-h-[72px] items-center gap-3">
            <img src={ICONS.schedule} alt="" className="h-6 w-6 shrink-0 opacity-80" draggable="false" />
            <div className="min-w-0 flex-1">
              <div className="text-body-large text-on-surface">
                {formatDisplayTime(selectedTime)} - {formatDisplayTime(endTime)}
              </div>
              <div className="text-body-medium text-on-surface-var truncate">
                {selectedDate
                  ? formatDate(selectedDate, {
                      weekday: "long",
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })
                  : "—"}
              </div>
            </div>
          </div>

          <Divider inset variant="outlineVariant" />

          <div className="px-4 py-4 flex items-center gap-3">
            <img src={ICONS.type} alt="" className="h-6 w-6 shrink-0 opacity-80" draggable="false" />
            <div className="min-w-0 flex-1 text-body-large text-on-surface">{typeLabel}</div>
            <div className="text-body-large text-on-surface-var">{amountLabel}</div>
          </div>

          <Divider inset variant="outlineVariant" />

          <div className="px-4 py-4 flex items-center gap-3">
            <img src={placeIcon} alt="" className="h-6 w-6 shrink-0 opacity-80" draggable="false" />
            <div className="min-w-0 flex-1 text-body-large text-on-surface truncate">{placeLabel}</div>
          </div>
        </Card>

        {submitError ? (
          <div className="mt-4 rounded-[10px] bg-[rgba(220,38,38,0.08)] px-3 py-2 text-body-medium text-[rgb(var(--schemes-critical))]">
            {submitError}
          </div>
        ) : null}

        <div className="mt-6 flex justify-center">
          <Button
            size="cta"
            className="w-full md:w-auto md:min-w-[380px]"
            disabled={!canConfirmReschedule || isSubmitting}
            onClick={handleRescheduleConfirm}
          >
            {isSubmitting
              ? t("appointments.confirm.confirming", "Confirming...")
              : t("appointments.confirm.confirm", "Confirm")}
          </Button>
        </div>
      </div>
    </div>
  );
}
